import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  Typography,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { apiService } from '../services/api';
import { Upload, Delete, Folder, AttachFile } from '@mui/icons-material';
import { PHONE_REGION_CODES, COUNTRIES, CURRENCIES } from '../data/constants';
import { EmployeeAccess } from '../types';

interface Entity {
  id: string;
  name: string;
}

interface Department {
  id: string;
  name: string;
}

interface Position {
  id: string;
  title: string;
}

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  nickname?: string;
  fullName: string;
}

interface AddEmployeeModalProps {
  open: boolean;
  onClose: () => void;
  onEmployeeAdded: () => void;
}

const AddEmployeeModal: React.FC<AddEmployeeModalProps> = ({
  open,
  onClose,
  onEmployeeAdded,
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    nickname: '',
    email: '',
    phoneNumber: '',
    phoneRegionCode: '+852',
    country: '',
    entityId: '',
    departmentId: '',
    positionId: '',
    positionTitle: '',
    managerId: '',
    startDate: new Date().toISOString().split('T')[0],
    employmentStatus: 'active',
    workLocation: 'office',
    workMode: '',
    access: '',
    createUserAccount: true,
    userPassword: '',
    userRole: 'employee',
  });

  const [entities, setEntities] = useState<Entity[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [managers, setManagers] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dataLoading, setDataLoading] = useState(true);

  // Document upload queue
  const [docQueue, setDocQueue] = useState<{ file: File; category: string; description: string }[]>([]);
  const [docCategory, setDocCategory] = useState('other');
  const [docDescription, setDocDescription] = useState('');
  const [generatedPassword, setGeneratedPassword] = useState<{ email: string; password: string } | null>(null);

  // Salary state
  const [salaryAmount, setSalaryAmount] = useState('');
  const [salaryCurrency, setSalaryCurrency] = useState('HKD');

  const DOC_CATEGORIES = [
    { value: 'id', label: 'ID Document' },
    { value: 'contract', label: 'Contract' },
    { value: 'certificate', label: 'Certificate' },
    { value: 'visa', label: 'Visa / Work Permit' },
    { value: 'payslip', label: 'Payslip' },
    { value: 'other', label: 'Other' },
  ];

  const handleAddDocToQueue = (file: File) => {
    setDocQueue(prev => [...prev, { file, category: docCategory, description: docDescription }]);
    setDocDescription('');
  };

  const handleRemoveDoc = (index: number) => {
    setDocQueue(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    if (open) {
      loadInitialData();
      loadManagers();
    }
  }, [open]);

  useEffect(() => {
    if (formData.entityId) {
      loadDepartments();
      loadPositions();
    }
  }, [formData.entityId]);

  const loadInitialData = async () => {
    try {
      setDataLoading(true);
      const entitiesData = await apiService.getEntities();
      setEntities(entitiesData);
    } catch (err) {
      console.error('Error loading entities:', err);
      setError('Failed to load entities');
    } finally {
      setDataLoading(false);
    }
  };

  const loadDepartments = async () => {
    if (!formData.entityId) return;
    try {
      const deptData = await apiService.getDepartments(formData.entityId);
      setDepartments(deptData);
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const loadPositions = async () => {
    if (!formData.entityId) return;
    try {
      const posData = await apiService.getPositions(formData.entityId);
      setPositions(posData);
    } catch (err) {
      console.error('Error loading positions:', err);
    }
  };

  const loadManagers = async () => {
    try {
      const empData = await apiService.getEmployees(); // all entities — cross-entity managers allowed
      setManagers(empData.filter((e: any) => e.employmentStatus === 'active'));
    } catch (err) {
      console.error('Error loading managers:', err);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const generateRandomPassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    handleInputChange('userPassword', password);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Auto-create position if typed freely and no existing ID matched
      let resolvedPositionId = formData.positionId;
      if (!resolvedPositionId && formData.positionTitle) {
        try {
          const newPos = await apiService.createPosition({
            title: formData.positionTitle,
            departmentId: formData.departmentId || undefined,
            entityId: formData.entityId || undefined,
          });
          resolvedPositionId = newPos.id;
        } catch (e) {
          console.warn('Could not auto-create position, proceeding without positionId');
        }
      }

      const { positionTitle, positionId: _pid, userPassword, ...rest } = formData;
      // Strip empty strings so backend validators don't choke on them
      const cleaned = Object.fromEntries(
        Object.entries(rest).filter(([_, v]) => v !== '' && v !== null && v !== undefined)
      );
      const employeeData: any = {
        ...cleaned,
        positionId: resolvedPositionId || undefined,
        managerId: formData.managerId || undefined,
        ...(salaryAmount && Number(salaryAmount) > 0 ? {
          monthlySalary: Number(salaryAmount),
          salaryCurrency: salaryCurrency || 'HKD',
        } : {}),
      };
      // Only include userPassword if it's non-empty
      if (userPassword && userPassword.length >= 6) {
        employeeData.userPassword = userPassword;
      } else {
        // If no valid password, don't create user account
        employeeData.createUserAccount = false;
      }

      // Create employee with optional user account
      const createdEmp = await apiService.createEmployee(employeeData);


      // Upload queued documents after employee is created
      if (docQueue.length > 0) {
        await Promise.all(docQueue.map(d =>
          apiService.uploadDocument(createdEmp.id, d.file, d.category, d.description).catch(() => {})
        ));
      }

      onEmployeeAdded();

      // Show generated password dialog before closing
      if (createdEmp.generatedPassword || createdEmp.user?.generatedPassword) {
        const pwd = createdEmp.generatedPassword || createdEmp.user?.generatedPassword;
        const email = formData.email || createdEmp.email;
        setGeneratedPassword({ email, password: pwd });
      } else {
        onClose();
      }

      setFormData({
        firstName: '',
        lastName: '',
        nickname: '',
        email: '',
        phoneNumber: '',
        phoneRegionCode: '+852',
        country: '',
        entityId: '',
        departmentId: '',
        positionId: '',
      positionTitle: '',
        managerId: '',
        startDate: new Date().toISOString().split('T')[0],
        employmentStatus: 'active',
        workLocation: 'office',
        workMode: '',
        access: '',
        createUserAccount: true,
        userPassword: '',
        userRole: 'employee',
      });
      setSalaryAmount('');
      setSalaryCurrency('HKD');
      setDocQueue([]);
    } catch (err: any) {
      console.error('Error creating employee:', err);
      setError(err.message || 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = formData.firstName && formData.lastName && formData.email && 
                     formData.entityId && formData.departmentId && formData.positionTitle;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Employee</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {dataLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Nickname"
                value={formData.nickname}
                onChange={(e) => handleInputChange('nickname', e.target.value)}
                helperText="Display name (optional)"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Autocomplete
                options={PHONE_REGION_CODES}
                getOptionLabel={(option) => `${option.flag} ${option.code} (${option.country})`}
                value={PHONE_REGION_CODES.find(r => r.code === formData.phoneRegionCode) || null}
                onChange={(event, newValue) => {
                  handleInputChange('phoneRegionCode', newValue?.code || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region Code"
                    helperText="Select region code"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                placeholder="12345678"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={COUNTRIES}
                getOptionLabel={(option) => option.name}
                value={COUNTRIES.find(c => c.name === formData.country) || null}
                onChange={(event, newValue) => {
                  handleInputChange('country', newValue?.name || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country (Based In)"
                    helperText="Country where employee is based"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.code === value.code}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={entities}
                getOptionLabel={(option) => option.name}
                value={entities.find(e => e.id === formData.entityId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('entityId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Entity"
                    required
                    helperText="Select entity"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={departments}
                getOptionLabel={(option) => option.name}
                value={departments.find(d => d.id === formData.departmentId) || null}
                onChange={(event, newValue) => {
                  handleInputChange('departmentId', newValue?.id || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Department"
                    required
                    helperText="Select department"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!formData.entityId}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                freeSolo
                options={positions}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.title}
                value={formData.positionTitle || null}
                onInputChange={(event, newValue) => {
                  handleInputChange('positionTitle', newValue || '');
                  const match = positions.find(p => p.title.toLowerCase() === (newValue || '').toLowerCase());
                  handleInputChange('positionId', match?.id || '');
                }}
                onChange={(event, newValue) => {
                  if (typeof newValue === 'object' && newValue) {
                    handleInputChange('positionId', newValue.id);
                    handleInputChange('positionTitle', newValue.title);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Position"
                    required
                    helperText="Type or choose from existing positions"
                  />
                )}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={managers}
                getOptionLabel={(option) => option ? `${option.nickname || ''} ${option.lastName || ''}`.trim() : ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={managers.find(m => m.id === formData.managerId) || null}
                onChange={(_, newValue) => handleInputChange('managerId', newValue?.id || '')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Manager"
                    placeholder="Search by nickname"
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={[
                  { value: 'active', label: 'Active' },
                  { value: 'on_leave', label: 'On Leave' },
                  { value: 'terminated', label: 'Terminated' }
                ]}
                getOptionLabel={(option) => option.label}
                value={{ value: formData.employmentStatus, label: formData.employmentStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
                onChange={(event, newValue) => {
                  handleInputChange('employmentStatus', newValue?.value || 'active');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employment Status"
                    required
                    helperText="Select employment status"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={[
                  { value: 'office', label: 'Office' },
                  { value: 'remote', label: 'Remote' },
                  { value: 'hybrid', label: 'Hybrid' }
                ]}
                getOptionLabel={(option) => option.label}
                value={{ value: formData.workLocation, label: formData.workLocation.charAt(0).toUpperCase() + formData.workLocation.slice(1) }}
                onChange={(event, newValue) => {
                  handleInputChange('workLocation', newValue?.value || 'office');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Work Location"
                    helperText="Select work location"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.access === EmployeeAccess.APPROVER}
                      onChange={(e) => handleInputChange('access', e.target.checked ? EmployeeAccess.APPROVER : '')}
                    />
                  }
                  label="Approver"
                />
              </FormControl>
            </Grid>

            {/* Work Mode */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Work Mode</InputLabel>
                <Select
                  value={formData.workMode}
                  onChange={(e) => handleInputChange('workMode', e.target.value)}
                  label="Work Mode"
                >
                  <MenuItem value="">— Not set —</MenuItem>
                  <MenuItem value="onsite">Onsite</MenuItem>
                  <MenuItem value="remote">Remote</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Monthly Salary */}
            <Grid size={12}><Divider sx={{ mt: 1 }} /></Grid>
            <Grid size={12}>
              <Typography variant="subtitle2" fontWeight="bold">Monthly Salary</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={salaryCurrency}
                  onChange={(e) => setSalaryCurrency(e.target.value)}
                  label="Currency"
                >
                  {CURRENCIES.map((c: any) => (
                    <MenuItem key={c.code} value={c.code}>{c.code} — {c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                fullWidth
                label="Monthly Salary"
                type="number"
                value={salaryAmount}
                onChange={(e) => setSalaryAmount(e.target.value)}
                placeholder="e.g. 30000"
              />
            </Grid>

            {/* Documents Upload Section */}
            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="h6" sx={{ mt: 1, mb: 1.5 }}>
                📎 Attachments
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>(optional — uploaded when employee is created)</Typography>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: docQueue.length > 0 ? 1.5 : 0 }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Category</InputLabel>
                  <Select value={docCategory} onChange={e => setDocCategory(e.target.value)} label="Grade">
                    {DOC_CATEGORIES.map(cat => <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" label="Description (optional)" value={docDescription} onChange={e => setDocDescription(e.target.value)} sx={{ flex: 1, minWidth: 140 }} />
                <Button variant="contained" component="label" startIcon={<AttachFile />} sx={{ whiteSpace: 'nowrap' }}>
                  Attach File
                  <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleAddDocToQueue(f); e.target.value = ''; }} />
                </Button>
              </Box>
              {docQueue.length > 0 && (
                <List dense disablePadding sx={{ bgcolor: '#f5f8ff', borderRadius: 1, border: '1px solid #d0d9f0' }}>
                  {docQueue.map((d, i) => (
                    <ListItem key={i} dense secondaryAction={
                      <IconButton size="small" edge="end" onClick={() => handleRemoveDoc(i)}><Delete fontSize="small" /></IconButton>
                    }>
                      <ListItemIcon sx={{ minWidth: 32 }}><AttachFile fontSize="small" color="primary" /></ListItemIcon>
                      <ListItemText
                        primary={d.file.name}
                        secondary={`${DOC_CATEGORIES.find(c => c.value === d.category)?.label}${d.description ? ' · ' + d.description : ''}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                  <ListItem dense>
                    <ListItemText
                      secondary={`✅ ${docQueue.length} file${docQueue.length > 1 ? 's' : ''} ready — will upload automatically when employee is created`}
                      secondaryTypographyProps={{ variant: 'caption', color: 'primary' }}
                    />
                  </ListItem>
                </List>
              )}
            </Grid>
            <Grid size={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            {/* User Account Creation Section */}
            <Grid size={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                User Account Settings
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                options={[
                  { value: true, label: 'Yes' },
                  { value: false, label: 'No' }
                ]}
                getOptionLabel={(option) => option.label}
                value={{ value: formData.createUserAccount, label: formData.createUserAccount ? 'Yes' : 'No' }}
                onChange={(event, newValue) => {
                  handleInputChange('createUserAccount', newValue?.value || false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Create User Account"
                    helperText="Allow employee to log into the system"
                  />
                )}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                autoHighlight
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
              />
            </Grid>
            {formData.createUserAccount && (
              <>
                <Grid size={{ xs: 12 }}>
                  <Alert severity="info">
                    A secure password will be automatically generated for this user. You can view and copy the password from the Access Control table after creation.
                  </Alert>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Autocomplete
                    options={[
                      { value: 'employee', label: 'Employee' },
                      { value: 'hr_admin', label: 'HR Admin' }
                    ]}
                    getOptionLabel={(option) => option.label}
                    value={{ value: formData.userRole, label: formData.userRole === 'hr_admin' ? 'HR Admin' : 'Employee' }}
                    onChange={(event, newValue) => {
                      handleInputChange('userRole', newValue?.value || 'employee');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="User Role"
                        required={formData.createUserAccount}
                        helperText="System access level"
                      />
                    )}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    autoHighlight
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                  />
                </Grid>
              </>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !isFormValid || dataLoading}
        >
          {loading ? <CircularProgress size={20} /> : 'Add Employee'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Generated Password Dialog */}
    
    <Dialog open={!!generatedPassword} maxWidth="sm" fullWidth>
      <DialogTitle>🔐 Account Created Successfully</DialogTitle>
      <DialogContent>
        <Typography gutterBottom>
          Share these login credentials with the employee:
        </Typography>
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>Email</Typography>
          <Typography variant="body1" fontWeight={600} gutterBottom>{generatedPassword?.email}</Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>Password</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="h6"
              fontFamily="monospace"
              sx={{ bgcolor: 'warning.light', px: 2, py: 0.5, borderRadius: 1, letterSpacing: 2 }}
            >
              {generatedPassword?.password}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(generatedPassword?.password || '');
              }}
            >
              Copy
            </Button>
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          ⚠️ This password will not be shown again. Please copy and share it now.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            setGeneratedPassword(null);
            onClose();
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AddEmployeeModal;